package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.draganddrop.DragAndDropEvent
import androidx.compose.ui.draganddrop.DragAndDropTransferData
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.DataTransfer
import org.w3c.dom.events.Event

// Same in-process shortcut as iOS: a PairMatching drag is always same-tab and exactly one
// drag is active at a time, so we stash the id when the transfer is built and read it back on drop.
private var inFlightValueId: Int? = null

private fun newDataTransfer(): DataTransfer = js("new DataTransfer()")

@OptIn(ExperimentalComposeUiApi::class)
actual fun pairMatchingTransferData(valueId: Int): DragAndDropTransferData {
    inFlightValueId = valueId
    PointerYTracker.ensureStarted()
    val dataTransfer = newDataTransfer()
    dataTransfer.setData("text/plain", "pair-matching-value-id:$valueId")
    return DragAndDropTransferData(dataTransfer)
}

actual fun DragAndDropEvent.readPairMatchingValueId(): Int? = inFlightValueId

actual fun DragAndDropEvent.isPairMatchingDrag(): Boolean = inFlightValueId != null

// The event itself carries no position on web, so the pointer is followed at the window level
// instead. Root-relative pixels, like LayoutCoordinates.boundsInRoot() used on the column.
actual fun DragAndDropEvent.dragYInRoot(): Float = PointerYTracker.yInRoot()

/** Follows the pointer's vertical position while a drag is going on, whatever element it is over. */
private object PointerYTracker {
    private var started = false
    private var clientY = Double.NaN

    private val listener: (Event) -> Unit = { event ->
        val y = event.asDynamic().clientY
        if (y != null) clientY = (y as Number).toDouble()
    }

    fun ensureStarted() {
        if (started) return
        started = true
        // "dragover" covers a native HTML drag, during which browsers send no pointer events.
        listOf("pointermove", "dragover").forEach { type ->
            window.addEventListener(type, listener, true)
        }
    }

    fun yInRoot(): Float {
        if (clientY.isNaN()) return Float.NaN
        val rootTop = document.getElementById("compose-container")?.getBoundingClientRect()?.top ?: 0.0
        return ((clientY - rootTop) * window.devicePixelRatio).toFloat()
    }
}
